import {
  useCallback,
  useState,
  useEffect,
  useRef,
  useMemo,
  useLayoutEffect,
} from "react";

import {
  HubConnection,
  HubConnectionBuilder,
  LogLevel,
  HubConnectionState,
} from "@microsoft/signalr";

import ordersApi from "services/ordersApi";
import { useLocation } from "react-router-dom";

export interface MessageData {
  orderId: string;
  fromId: string;
  toId: string;
  message: string;
  time?: string;
  name?: string;
}

interface OrdersApiChatResponse {
  messages: {
    orderId: string;
    fromId: string;
    toId: string;
    message: string;
    time?: string;
    name?: string;
  }[];
}

export const useChat = (token: string) => {
  const [chatData, setChatData] = useState<never[] | MessageData[]>([]);
  const [dataUser, setDataUser] = useState<any>();

  const isMountedRef = useRef(true);
  const isInitializingRef = useRef(false);
  const hubConnection = useRef<HubConnection | null>(null);

  function useQuery() {
    const { search } = useLocation();

    return useMemo(() => new URLSearchParams(search), [search]);
  }

  const orderId = useQuery().get("order-id");
  const dealerId = useQuery().get("dealer-id");
  const consumerId = useQuery().get("consumer-id");

  const handleSendNewMessage = useCallback((message: MessageData) => {
    hubConnection.current?.invoke("SendMessage", message);
  }, []);

  const handleGetInProgressChat = useCallback(async () => {
    const chatInitialData = await ordersApi.get<OrdersApiChatResponse>(
      `${process.env.REACT_APP_ORDERS_API_URL}v1/orders/${orderId}/chat`,
      {
        headers: {
          authorization: `Bearer ${token}`,
        },
      },
    );

    const allChats: MessageData[] = [];

    if (chatInitialData.data.messages) {
      chatInitialData.data.messages.forEach((chat) => allChats.push(chat));
    }

    setChatData(allChats);
  }, [orderId, token]);

  const handleGetData = useCallback(async () => {
    const chatInitialData = await ordersApi.get<any>(
      `${process.env.REACT_APP_DEALERCARE_API_URL}v1/dealers/${dealerId}`,
      {
        headers: {
          authorization: `Bearer ${token}`,
        },
      },
    );

    setDataUser(chatInitialData?.data?.data);
  }, [dealerId, token]);

  useEffect(() => {
    handleGetData();
    // eslint-disable-next-line
  }, []);

  const closeConnection = useCallback(async () => {
    if (
      hubConnection.current &&
      hubConnection.current.state === HubConnectionState.Connected
    ) {
      try {
        hubConnection.current.off("MessageReceived");

        await hubConnection.current.stop();
      } catch (error) {
        console.error("error ao fechar a conexão", error);
      } finally {
        hubConnection.current = null;
      }
    }
  }, []);

  const initializeConnection = useCallback(async () => {
    if (isInitializingRef.current || !isMountedRef.current || !consumerId) {
      return;
    }

    isInitializingRef.current = true;

    try {
      await closeConnection();

      hubConnection.current = new HubConnectionBuilder()
        .withUrl(`${process.env.REACT_APP_ORDERS_API_URL}order-hubs/chat`, {
          withCredentials: false,
        })
        .configureLogging(
          process.env.NODE_ENV === "development"
            ? LogLevel.Information
            : LogLevel.Error,
        )
        .withAutomaticReconnect([
          0, 3000, 5000, 10000, 15000, 30000, 60000, 120000,
        ])
        .build();

      hubConnection.current.onreconnected(async () => {
        if (
          isMountedRef.current &&
          hubConnection.current &&
          hubConnection.current.state === HubConnectionState.Connected
        ) {
          await hubConnection.current.invoke("JoinChat", consumerId);
        }
      });

      hubConnection.current.onclose(async () => {
        if (isMountedRef.current) {
          await initializeConnection();
        }
      });

      hubConnection.current.on("MessageReceived", (data) => {
        setChatData((prevData) => [...prevData, data]);
      });

      await hubConnection.current.start();
      await hubConnection.current.invoke("JoinChat", consumerId);
    } catch (error) {
      console.error("error ao iniciar o SignalR:", error);

      const retryDelay = Math.min(5000 * 2 ** Math.random(), 30000);

      setTimeout(() => {
        if (isMountedRef.current) {
          initializeConnection();
        }
      }, retryDelay);
      return;
    } finally {
      isInitializingRef.current = false;
    }
  }, [consumerId, closeConnection]);

  useEffect(() => {
    isMountedRef.current = true;

    initializeConnection();

    return () => {
      isMountedRef.current = false;
      closeConnection();
    };
  }, [consumerId, initializeConnection, closeConnection]);

  useLayoutEffect(() => {
    handleGetInProgressChat();
  }, [handleGetInProgressChat]);

  return { chatData, dataUser, handleSendNewMessage };
};
